/* fbt-build-1779812902 */
/*
  Frequently Bought Together
  Custom element that powers the TKBE native FBT section.

  Responsibilities:
    1. Hydrate missing recommendation rows from Shopify's endpoints
       (complementary -> related -> /products.json random).
    2. Recalculate bundle total, compare-at total and savings on checkbox
       or variant changes.
    3. Submit the bundle to /cart/add.js in a single request.
    4. Record a conversion via cart note attribute for order-level tracking.
    5. Open the theme's cart drawer (or cart-notification) after adding,
       with a /cart redirect fallback.
*/

(function () {
  if (customElements.get('fbt-bundle')) return;

  var SAMPLE_SUFFIX = '-SA';
  var TRACKING_KEY = '__frequently-bought-together-added';

  function endsWithSample(sku) {
    if (!sku) return false;
    return String(sku).slice(-SAMPLE_SUFFIX.length) === SAMPLE_SUFFIX;
  }

  function toCents(value) {
    // Shopify AJAX endpoints report prices as decimal strings on /products.json
    // and /recommendations/products.json, but as cents on /cart.js.
    // This helper coerces either to cents.
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') {
      // If integer-like and large, it's already in cents; if it has decimals, coerce.
      return Number.isInteger(value) ? value : Math.round(value * 100);
    }
    var str = String(value);
    if (str.indexOf('.') === -1) return parseInt(str, 10) || 0;
    return Math.round(parseFloat(str) * 100);
  }

  function escapeHtml(s) {
    var div = document.createElement('div');
    div.textContent = s == null ? '' : String(s);
    return div.innerHTML;
  }

  function sizedImage(src, width) {
    if (!src) return '';
    // Shopify CDN supports ?width= and will ignore it on non-CDN URLs.
    if (src.indexOf('//') === 0) src = 'https:' + src;
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function resolveImage(product) {
    if (!product) return '';
    if (product.featured_image) {
      if (typeof product.featured_image === 'string') return product.featured_image;
      if (product.featured_image.src) return product.featured_image.src;
    }
    if (product.images && product.images.length > 0) {
      var first = product.images[0];
      if (typeof first === 'string') return first;
      if (first && first.src) return first.src;
    }
    return '';
  }

  function resolveOptionName(product) {
    if (!product || !product.options) return '';
    var opt = product.options[0];
    if (!opt) return '';
    if (typeof opt === 'string') return opt;
    if (opt.name) return opt.name;
    return '';
  }

  var FbtBundle = function () {
    return Reflect.construct(HTMLElement, [], FbtBundle);
  };
  Object.setPrototypeOf(FbtBundle.prototype, HTMLElement.prototype);
  Object.setPrototypeOf(FbtBundle, HTMLElement);

  FbtBundle.prototype.connectedCallback = function () {
    if (this._initialised) return;
    this._initialised = true;

    this.rowsEl       = this.querySelector('[data-fbt-rows]');
    this.summaryEl    = this.querySelector('[data-fbt-summary]');
    this.totalEl      = this.querySelector('[data-fbt-total]');
    this.compareEl    = this.querySelector('[data-fbt-total-compare]');
    this.savingsEl    = this.querySelector('[data-fbt-savings]');
    this.bundleDiscEl = this.querySelector('[data-fbt-bundle-discount]');
    this.submitBtn    = this.querySelector('[data-fbt-submit]');
    this.submitLbl    = this.submitBtn ? this.submitBtn.querySelector('.fbt__submit-label') : null;
    this.spinnerEl    = this.submitBtn ? this.submitBtn.querySelector('.fbt__submit-spinner') : null;

    this.productId  = this.dataset.productId;
    this.moneyFormat = this.dataset.moneyFormat || '';
    this.currency   = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
    this.locale     = document.documentElement.lang || 'en-GB';

    // Bundle discount percentage from source-product metafield. 0 = no
    // bundle discount on this source. Display-only; the actual checkout
    // discount is set up as a Shopify automatic discount by the merchant.
    this.bundleDiscountPct = parseFloat(this.dataset.bundleDiscountPct) || 0;

    this.addedIds = new Set();
    this.addedIds.add(String(this.productId));
    var self = this;
    this.rowsEl.querySelectorAll('.fbt__row[data-product-id]').forEach(function (row) {
      if (row.dataset.productId) self.addedIds.add(String(row.dataset.productId));
    });

    this.bindEvents();
    this.recalc();
    this.hydrate();
  };

  FbtBundle.prototype.bindEvents = function () {
    var self = this;
    this.rowsEl.addEventListener('change', function (e) {
      var t = e.target;
      if (t.classList.contains('fbt__checkbox')) {
        self.recalc();
      } else if (t.matches('[data-fbt-variant]')) {
        self.onVariantChange(t);
      }
    });
    this.submitBtn.addEventListener('click', function () { self.submitBundle(); });
  };

  FbtBundle.prototype.onVariantChange = function (select) {
    var row = select.closest('.fbt__row');
    if (!row) return;
    var opt = select.options[select.selectedIndex];
    var price = parseInt(opt.dataset.price, 10) || 0;
    var compareAt = parseInt(opt.dataset.compareAt, 10) || 0;

    row.dataset.variantId = opt.value;
    row.dataset.price = String(price);
    if (compareAt > price) {
      row.dataset.compareAt = String(compareAt);
    } else {
      delete row.dataset.compareAt;
    }

    var cb = row.querySelector('.fbt__checkbox');
    if (cb) cb.value = opt.value;

    // Swap variant image on row and the matching summary tile
    var productId = row.dataset.productId;
    var rowImageSrc = opt.dataset.image;
    var summaryImageSrc = opt.dataset.imageLarge || opt.dataset.image;
    if (rowImageSrc) {
      var rowImg = row.querySelector('.fbt__row-image img');
      if (rowImg) rowImg.src = rowImageSrc;
    }
    if (summaryImageSrc && productId) {
      var tile = this.summaryEl.querySelector('.fbt__summary-tile[data-product-id="' + productId + '"]');
      if (tile) {
        var tileImg = tile.querySelector('img');
        if (tileImg) tileImg.src = summaryImageSrc;
      }
    }

    this.paintRowPrice(row);
    this.recalc();
  };

  FbtBundle.prototype.paintRowPrice = function (row) {
    var cluster = row.querySelector('.fbt__row-price');
    if (!cluster) return;
    var price = parseInt(row.dataset.price, 10) || 0;
    var compareAt = parseInt(row.dataset.compareAt, 10) || 0;
    if (compareAt > price) {
      cluster.innerHTML =
        '<span class="fbt__price-compare">' + escapeHtml(this.formatMoney(compareAt)) + '</span>' +
        '<span class="fbt__price-current fbt__price-current--sale">' + escapeHtml(this.formatMoney(price)) + '</span>';
    } else {
      cluster.innerHTML =
        '<span class="fbt__price-current">' + escapeHtml(this.formatMoney(price)) + '</span>';
    }
  };

  FbtBundle.prototype.formatMoney = function (cents) {
    try {
      return new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: this.currency
      }).format(cents / 100);
    } catch (e) {
      return '£' + (cents / 100).toFixed(2);
    }
  };

  FbtBundle.prototype.countPlaceholders = function () {
    return this.rowsEl.querySelectorAll('[data-fbt-row-placeholder]').length;
  };

  FbtBundle.prototype.hydrate = function () {
    var needed = this.countPlaceholders();
    if (needed <= 0) return;

    var self = this;
    var urls = [
      '/recommendations/products.json?product_id=' + encodeURIComponent(this.productId) + '&intent=complementary&limit=10',
      '/recommendations/products.json?product_id=' + encodeURIComponent(this.productId) + '&intent=related&limit=10',
      '/products.json?limit=20'
    ];

    var chain = Promise.resolve();
    urls.forEach(function (url) {
      chain = chain.then(function () {
        if (self.countPlaceholders() <= 0) return;
        return fetch(url, { credentials: 'same-origin' })
          .then(function (res) { return res.ok ? res.json() : null; })
          .then(function (json) {
            if (!json || !json.products) return;
            for (var i = 0; i < json.products.length; i++) {
              if (self.countPlaceholders() <= 0) break;
              var p = json.products[i];
              if (!self.acceptProduct(p)) continue;
              self.addRecRow(p);
            }
          })
          .catch(function () { /* continue to next fallback */ });
      });
    });

    chain.then(function () {
      // Any still-empty placeholders mean we genuinely couldn't find enough products.
      // Remove them so the section reads cleanly. For summary tiles, also remove
      // the adjacent '+' separator so we don't leave orphan plus signs.
      self.rowsEl.querySelectorAll('[data-fbt-row-placeholder]').forEach(function (p) { p.remove(); });
      self.summaryEl.querySelectorAll('[data-fbt-summary-placeholder]').forEach(function (p) {
        var prev = p.previousElementSibling;
        if (prev && prev.classList.contains('fbt__plus')) prev.remove();
        p.remove();
      });
      self.recalc();
    });
  };

  FbtBundle.prototype.acceptProduct = function (p) {
    if (!p || !p.id) return false;
    if (this.addedIds.has(String(p.id))) return false;
    if (p.available === false) return false;
    var variants = p.variants || [];
    var firstAvail = null;
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].available) { firstAvail = variants[i]; break; }
    }
    if (!firstAvail) return false;
    if (endsWithSample(firstAvail.sku)) return false;
    if (!resolveImage(p)) return false;
    return true;
  };

  FbtBundle.prototype.addRecRow = function (p) {
    var variants = p.variants || [];
    var firstAvail = null;
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].available) { firstAvail = variants[i]; break; }
    }
    if (!firstAvail) return;

    var price = toCents(firstAvail.price);
    var compareAt = firstAvail.compare_at_price ? toCents(firstAvail.compare_at_price) : 0;

    var availableVariants = variants.filter(function (v) {
      return v.available && !endsWithSample(v.sku);
    });
    var optionCount = (p.options || []).length;
    var needsSelect = optionCount === 1 && availableVariants.length > 1;
    var optionName = resolveOptionName(p);

    var productImage = resolveImage(p);
    var selectHtml = '';
    if (needsSelect) {
      selectHtml += '<select class="fbt__row-variant-select" aria-label="' + escapeHtml(optionName) + '" data-fbt-variant>';
      availableVariants.forEach(function (v) {
        var vPrice = toCents(v.price);
        var vCompare = v.compare_at_price ? toCents(v.compare_at_price) : vPrice;
        var vImgSrc = (v.featured_image && v.featured_image.src) || productImage;
        selectHtml +=
          '<option value="' + v.id + '"' +
          ' data-price="' + vPrice + '"' +
          ' data-compare-at="' + vCompare + '"' +
          ' data-image="' + sizedImage(vImgSrc, 160) + '"' +
          ' data-image-large="' + sizedImage(vImgSrc, 240) + '"' +
          (v.id === firstAvail.id ? ' selected' : '') + '>' +
          escapeHtml(v.title) +
          '</option>';
      });
      selectHtml += '</select>';
    }

    var imageSrc = sizedImage(resolveImage(p), 160);
    var summarySrc = sizedImage(resolveImage(p), 240);
    var productUrl = '/products/' + encodeURIComponent(p.handle);

    var li = document.createElement('li');
    li.className = 'fbt__row';
    li.setAttribute('data-product-id', p.id);
    li.setAttribute('data-variant-id', firstAvail.id);
    li.setAttribute('data-price', String(price));
    if (compareAt > price) li.setAttribute('data-compare-at', String(compareAt));

    li.innerHTML =
      '<label class="fbt__row-label">' +
        '<input type="checkbox" class="fbt__checkbox" value="' + firstAvail.id + '"' +
          ' aria-label="Include ' + escapeHtml(p.title) + ' in bundle" checked>' +
        '<span class="fbt__row-image">' +
          '<img src="' + imageSrc + '" width="80" height="80" alt="' + escapeHtml(p.title) + '" loading="lazy">' +
        '</span>' +
        '<span class="fbt__row-info">' +
          '<a class="fbt__row-name" href="' + productUrl + '">' + escapeHtml(p.title) + '</a>' +
          selectHtml +
        '</span>' +
      '</label>' +
      '<span class="fbt__row-price"></span>';

    this.paintRowPrice(li);

    var placeholderRow = this.rowsEl.querySelector('[data-fbt-row-placeholder]');
    if (placeholderRow) placeholderRow.replaceWith(li);
    else this.rowsEl.appendChild(li);

    var placeholderTile = this.summaryEl.querySelector('[data-fbt-summary-placeholder]');
    if (placeholderTile) {
      var img = document.createElement('img');
      img.src = summarySrc;
      img.width = 120;
      img.height = 120;
      img.alt = '';
      img.loading = 'lazy';
      placeholderTile.classList.remove('fbt__summary-tile--placeholder');
      placeholderTile.removeAttribute('data-fbt-summary-placeholder');
      placeholderTile.setAttribute('data-product-id', p.id);
      placeholderTile.appendChild(img);
    }

    this.addedIds.add(String(p.id));
  };

  FbtBundle.prototype.recalc = function () {
    var rows = this.rowsEl.querySelectorAll('.fbt__row:not(.fbt__row--placeholder)');
    var current = 0;
    var compare = 0;
    var checked = 0;
    var total = 0;
    rows.forEach(function (row) {
      var cb = row.querySelector('.fbt__checkbox');
      if (!cb) return;
      total++;
      var price = parseInt(row.dataset.price, 10) || 0;
      var compareAt = parseInt(row.dataset.compareAt, 10) || price;
      if (cb.checked) {
        current += price;
        compare += compareAt;
        checked++;
        row.classList.remove('fbt__row--unchecked');
      } else {
        row.classList.add('fbt__row--unchecked');
      }
    });

    // Bundle discount only applies when EVERY row in the bundle is ticked.
    // Untick anything and the bundle is no longer eligible.
    var allChecked = total > 0 && checked === total;
    var bundleDiscountCents = 0;
    if (this.bundleDiscountPct > 0 && allChecked) {
      bundleDiscountCents = Math.round(current * this.bundleDiscountPct / 100);
    }
    var finalTotal = current - bundleDiscountCents;
    var compareSavings = Math.max(0, compare - current);
    // "Before" price shown struck through. Prefer the sum of compare-at prices
    // when any product is on sale (most generous "was" price), otherwise fall
    // back to the un-bundled current total so the bundle saving alone still
    // produces a visible strikethrough.
    var preDiscountTotal = compareSavings > 0 ? compare : current;
    var totalSavings = compareSavings + bundleDiscountCents;

    this.totalEl.textContent = this.formatMoney(finalTotal);

    if (totalSavings > 0 && checked > 0) {
      this.compareEl.textContent = this.formatMoney(preDiscountTotal);
      this.compareEl.hidden = false;
      this.totalEl.classList.add('fbt__total-value--sale');
    } else {
      this.compareEl.hidden = true;
      this.totalEl.classList.remove('fbt__total-value--sale');
    }

    // "You save £X" line is only useful when compare-at savings apply WITHOUT
    // a bundle discount. When the bundle discount is on, the bundle-deal line
    // below carries the messaging — two peach lines feels noisy.
    if (compareSavings > 0 && bundleDiscountCents === 0 && checked > 0) {
      this.savingsEl.textContent = 'You save ' + this.formatMoney(compareSavings);
      this.savingsEl.hidden = false;
    } else {
      this.savingsEl.hidden = true;
    }

    if (this.bundleDiscEl) {
      if (bundleDiscountCents > 0) {
        // The strikethrough total already shows the £ saving, so the bundle
        // line stays short and informational rather than a second number callout.
        this.bundleDiscEl.textContent =
          'Bundle deal: ' + this.bundleDiscountPct + '% off applied at checkout';
        this.bundleDiscEl.hidden = false;
      } else {
        this.bundleDiscEl.hidden = true;
      }
    }

    this.submitBtn.disabled = checked === 0;
    if (this.submitLbl) {
      this.submitLbl.textContent = checked === 0
        ? 'Select at least one item'
        : 'Add ' + checked + (checked === 1 ? ' item' : ' items') + ' to cart';
    }
  };

  FbtBundle.prototype.submitBundle = function () {
    var self = this;
    var rows = this.rowsEl.querySelectorAll('.fbt__row:not(.fbt__row--placeholder)');
    var items = [];
    rows.forEach(function (row) {
      var cb = row.querySelector('.fbt__checkbox');
      if (!cb || !cb.checked) return;
      var variantId = parseInt(row.dataset.variantId, 10);
      if (!variantId) return;
      items.push({ id: variantId, quantity: 1 });
    });
    if (items.length === 0) return;

    this.setLoading(true);

    var cartElement = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    // Mirror theme's product-form pattern: record the trigger so focus returns
    // correctly when the cart drawer/notification closes.
    if (cartElement && typeof cartElement.setActiveElement === 'function') {
      cartElement.setActiveElement(this.submitBtn);
    }
    var sectionsToRender = [];
    if (cartElement && typeof cartElement.getSectionsToRender === 'function') {
      sectionsToRender = cartElement.getSectionsToRender().map(function (s) { return s.id; });
    }

    var body = { items: items };
    if (sectionsToRender.length > 0) {
      body.sections = sectionsToRender;
      body.sections_url = window.location.pathname;
    }

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    })
      .then(function (res) {
        return res.json().then(function (json) {
          if (!res.ok) {
            var err = new Error(json && json.description ? json.description : 'Could not add bundle');
            err.response = json;
            throw err;
          }
          return json;
        });
      })
      .then(function (addJson) {
        // Fire tracking attribute, do not await; failures must not block the UX.
        var addedVariantIds = items.map(function (it) { return String(it.id); });
        self.recordConversion(addedVariantIds);

        if (cartElement && typeof cartElement.renderContents === 'function' && addJson.sections) {
          if (cartElement.classList.contains('is-empty')) {
            cartElement.classList.remove('is-empty');
          }
          cartElement.renderContents(addJson);
        } else if (cartElement && typeof cartElement.open === 'function') {
          cartElement.open();
        } else {
          window.location = window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart';
        }
      })
      .catch(function (err) {
        console.error('[fbt] add to cart failed', err);
        window.location = window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart';
      })
      .then(function () {
        self.setLoading(false);
      });
  };

  FbtBundle.prototype.recordConversion = function (addedVariantIds) {
    // Mirrors Code Black Belt's format: comma-separated variant IDs appended to
    // any prior FBT-added IDs on the cart, so multiple FBT uses in a session
    // accumulate rather than overwrite.
    if (!addedVariantIds || addedVariantIds.length === 0) return;
    var key = TRACKING_KEY;

    fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (cart) {
        var existing = (cart && cart.attributes && cart.attributes[key]) || '';
        var existingIds = existing ? String(existing).split(',').map(function (s) { return s.trim(); }).filter(Boolean) : [];
        var seen = Object.create(null);
        var merged = [];
        existingIds.concat(addedVariantIds).forEach(function (id) {
          if (!seen[id]) { seen[id] = true; merged.push(id); }
        });
        var attrs = {};
        attrs[key] = merged.join(',');
        return fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ attributes: attrs })
        });
      })
      .catch(function () { /* swallow, tracking is best-effort */ });
  };

  FbtBundle.prototype.setLoading = function (on) {
    if (!this.submitBtn) return;
    this.submitBtn.disabled = on;
    this.submitBtn.classList.toggle('loading', !!on);
    if (this.spinnerEl) this.spinnerEl.classList.toggle('hidden', !on);
  };

  customElements.define('fbt-bundle', FbtBundle);
})();
